import { StationeryProduct } from '@shared/utils/print-price/types';
import { useCallback } from 'react';
import { useProductToUIMapping } from '../../useProductToUIMapping';

const isString = (input: unknown): input is string => {
  return !!input && typeof input === 'string';
};

export const useDeriveFoilIDs = () => {
  const { getFoilColorId } = useGetFoilColorId();

  const deriveFoilIDs = useCallback(
    (product: StationeryProduct, attributeId: string): string[] => {
      const attributes = product.attributes;
      const targetAttribute = attributes.find(attr => attr.attributeId === attributeId);

      if (!targetAttribute) {
        return [];
      }

      const allFoilIDs = targetAttribute.attributeOptions.map(opt => getFoilColorId(attributeId, opt.attributeOption.id)).filter(isString);
      return allFoilIDs;
    },
    [getFoilColorId]
  );

  return { deriveFoilIDs };
};

type FoilValue = {
  colorId: string;
  assetThumbnailUrl: string;
};

export const ensureIsFoilValue = (value: unknown): value is FoilValue => {
  if (!value) {
    return false;
  }

  if (typeof value !== 'object') {
    return false;
  }

  if (!Object.hasOwn(value, 'colorId')) {
    return false;
  }

  if (!Object.hasOwn(value, 'assetThumbnailUrl')) {
    return false;
  }

  return true;
};

const useGetFoilColorId = () => {
  const { getOptionById } = useProductToUIMapping();

  const getFoilColorId = useCallback(
    (attributeId: string, optionId: string): string | null => {
      const option = getOptionById(attributeId, optionId);
      const value = option?.value;

      if (!ensureIsFoilValue(value)) {
        return null;
      }

      return value.colorId;
    },
    [getOptionById]
  );

  return { getFoilColorId };
};

const isValidFoil = (input: unknown): input is FoilValue & { optionId: string } => {
  return !!input;
};

export const useGetFoilConfigs = () => {
  const { getOptionById } = useProductToUIMapping();

  const getFoilConfigs = useCallback(
    (product: StationeryProduct, attributeId: string): (FoilValue & { optionId: string })[] | undefined => {
      const allOptions = product.attributes.find(a => a.attributeId === attributeId)?.attributeOptions;

      return allOptions
        ?.map(o => {
          if (!o) {
            return null;
          }

          const option = getOptionById(attributeId, o.attributeOption.id);
          const value = option?.value;

          if (!ensureIsFoilValue(value)) {
            return null;
          }

          return { ...value, optionId: o.attributeOption.id };
        })
        .filter(isValidFoil);
    },
    [getOptionById]
  );

  return { getFoilConfigs };
};
